import React from "react";
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Typography,
} from "@mui/material";
import { Dashboard, ChevronRight } from "@mui/icons-material";
import Logo from "../../assets/SJ-logo.png";
import { useNavigate, useLocation } from "react-router-dom";
import PersonIcon from "@mui/icons-material/Person";
import TextSnippetIcon from "@mui/icons-material/TextSnippet";
import MedicationIcon from "@mui/icons-material/Medication";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import MedicalServicesIcon from "@mui/icons-material/MedicalServices";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import StoreIcon from "@mui/icons-material/Store";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import NotificationsIcon from "@mui/icons-material/Notifications";
import CampaignIcon from "@mui/icons-material/Campaign";
import VerifiedOutlinedIcon from "@mui/icons-material/VerifiedOutlined";
import en_ja from "../../../src/translations/en_ja";
import "./sidebar.css";
import LinkIcon from '@mui/icons-material/Link';

const Sidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // parse user from localStorage (only once)
  const user = React.useMemo(() => {
    try {
      const raw = localStorage.getItem("user");
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  }, []);

  const roleName = user?.role?.roleName;
  const isSuperAdmin =
    typeof roleName === "string" && roleName.trim().toLowerCase() === "super admin";

  // generate menu items (memoized)
  const menuItems = React.useMemo(() => {
    const adminItem = [
      {
        id: "admin-management",
        label: en_ja["Admin Management"] ?? "Admin Management",
        icon: (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 512 512"
          >
            <path
              fill="currentColor"
              fillRule="evenodd"
              d="m213.33 28.445l72.568 96.757l106.085-26.521l-25.985 103.942c-20.742-2.159-42.612 4.795-58.788 22.769c-4.567 5.075-12.525 5.075-17.092 0c-38.594-42.882-109.598-23.038-120.366 33.64l-11.584 60.969H90.007l-55.33-221.32l106.085 26.521zm193.288 234.569c-7.475-39.342-56.761-53.117-83.551-23.351c-13.042 14.491-35.764 14.491-48.806 0c-26.789-29.766-76.076-15.991-83.551 23.351l-18.934 99.654h253.777zm22.988 120.987H167.723l-4.054 21.333H128v42.667h341.333v-42.667h-35.674z"
              clipRule="evenodd"
            />
          </svg>
        ), linkName: "admin-management",
      },
    ];

    const otherItems = [
      { id: "dashboard", label: en_ja["Dashboard"] ?? "Dashboard", icon: <Dashboard />, linkName: "dashboard" },
      { id: "clinic-management", label: en_ja["Clinic Management"] ?? "Clinic Management", icon: <MedicationIcon />, linkName: "clinic-management" },
      { id: "user-management", label: en_ja["User Management"] ?? "User Management", icon: <PersonIcon />, linkName: "user-management" },
      { id: "pharmacy-management", label: en_ja["Pharmacy Management"] ?? "Pharmacy Management", icon: <MedicalServicesIcon />, linkName: "pharmacy-management" },
      { id: "coupon-management", label: en_ja["Coupon Management"] ?? "Coupon Management", icon: <LocalOfferIcon />, linkName: "coupon-management" },
      { id: "banner-management", label: en_ja["Banner Management"] ?? "Banner Management", icon: <CampaignIcon />, linkName: "banner-management" },
      { id: "reports", label: en_ja["Reports"] ?? "Reports", icon: <TextSnippetIcon />, linkName: "reports" },
      { id: "warranty-management", label: en_ja["Warranty Management"] ?? "Warranty Management", icon: <VerifiedOutlinedIcon />, linkName: "warranty-management" },
      { id: "deeplink-management", label: en_ja["Deeplink Management"] ?? "Deeplink Management", icon: <LinkIcon />, linkName: "deeplink-management" },
      { id: "products", label: en_ja["Products"] ?? "Products", icon: <Inventory2Icon />, linkName: "products" },
      { id: "orders", label: en_ja["Orders"] ?? "Orders", icon: <ShoppingCartIcon />, linkName: "orders" },
      { id: "notifications", label: en_ja["Notifications"] ?? "Notifications", icon: <NotificationsIcon />, linkName: "notifications" },
      { id: "stores", label: en_ja["Stores"] ?? "Stores", icon: <StoreIcon />, linkName: "stores" },
    ];

    return isSuperAdmin ? adminItem : otherItems;
  }, [isSuperAdmin]);

  const isActiveRoute = (linkName) => {
    return location.pathname === `/${linkName}`;
  };
  const handleNavigation = (linkName) => {
    if (!linkName) return;
    navigate(`/${linkName}`);
  };

  // const handleLogout = () => {
  //   localStorage.removeItem("authToken");
  //   localStorage.removeItem("refreshToken");
  //   localStorage.removeItem("user");
  //   navigate("/login");
  // };

  return (
    <Paper elevation={0} className="sidebar-container">
      {/* Logo */}
      <Box
        className="sidebar-logo-section"
        sx={{ display: "flex", flexDirection: "column", alignItems: "center", width: "100%" }}
      >
        <Box
          component="img"
          src={Logo}
          alt={en_ja["Shop Japan Logo"] ?? "Shop Japan Logo"}
          className="sidebar-logo"
        />
        <Typography
          variant="caption"
          sx={{
            color: "text.secondary",
            fontSize: "0.75rem",
            fontWeight: 400,
            textAlign: "center",
            mt: 0.5,
            letterSpacing: "0.5px",
            textTransform: "uppercase",
            opacity: 0.7,
            display: "block",
            width: "100%",
          }}
        >
          {en_ja["Admin Portal"] ?? "Admin Portal"}
        </Typography>
      </Box>

      {/* Menu */}
      <Box className="sidebar-nav-section">
        <List className="sidebar-nav-list">
          {menuItems.map((item) => {
            const isActive = isActiveRoute(item.linkName);
            return (
              <ListItem key={item.id} disablePadding className="sidebar-nav-item">
                <ListItemButton
                  onClick={() => handleNavigation(item.linkName)}
                  className={`sidebar-nav-button ${isActive ? "active" : ""}`}
                >
                  <Box className="sidebar-nav-content">
                    <ListItemIcon className="sidebar-nav-icon">{item.icon}</ListItemIcon>

                    <ListItemText
                      primary={item.label}
                      primaryTypographyProps={{ className: "sidebar-nav-text" }}
                    />
                  </Box>

                  {item.hasArrow && <ChevronRight className="sidebar-nav-arrow" />}
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>
      </Box>
    </Paper>
  );
};

export default Sidebar;
